import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Phone, DeviceMobile, Copy, Eye, EyeSlash, CheckCircle, ArrowsClockwise,
  Headset, ShieldCheck, WifiHigh, Lightning,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Dialpad } from "@/components/Dialpad";

const Row = ({ label, value, mono = true, onCopy }) => (
  <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3">
    <div className="min-w-0">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`mt-0.5 truncate ${mono ? "font-mono-metric" : ""} text-sm text-foreground`} data-testid={`device-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{value}</p>
    </div>
    {onCopy && (
      <button onClick={onCopy} data-testid={`copy-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="shrink-0 rounded-lg p-2 text-muted-foreground hover:text-primary" aria-label={`Copy ${label}`}>
        <Copy size={16} />
      </button>
    )}
  </div>
);

const UsageBar = ({ label, used, total, suffix, money }) => {
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const fmt = (v) => money ? `$${(v / 100).toFixed(2)}` : `${v}${suffix ? ` ${suffix}` : ""}`;
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono-metric">{fmt(used)} / {fmt(total)}</span>
      </div>
      <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-secondary">
        <div className={`h-full rounded-full ${pct >= 100 ? "bg-red-500" : "bg-primary"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export default function DeviceSettings() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState("residential");
  const isOwner = user?.role === "platform_owner" || user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/telecom/account");
      setData(data);
      if (data?.provisioned) {
        try { const u = await api.get("/telecom/usage"); setUsage(u.data); } catch {}
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const copy = (v, label) => { navigator.clipboard.writeText(v).then(() => toast.success(`${label} copied`)); };

  const activate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/telecom/provision", { plan_key: plan });
      setData({ provisioned: true, ...data });
      toast.success("Your global line is live!");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const refreshToken = async () => {
    try { const { data: t } = await api.post("/telecom/webrtc-token"); setData((d) => ({ ...d, webrtc_token: t.webrtc_token })); toast.success("Softphone token refreshed"); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <div className="mx-auto max-w-2xl p-6 text-sm text-muted-foreground" data-testid="device-loading">Loading your device settings…</div>;

  // Not provisioned yet
  if (!data?.provisioned) {
    return (
      <div className="mx-auto max-w-2xl space-y-6 p-1" data-testid="device-settings-empty">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Device Settings</h1>
          <p className="mt-2 text-muted-foreground">Your global number, SIP desk-phone credentials and softphone live here — activated the moment you start a plan.</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-6 text-center">
          <Phone size={40} weight="fill" className="mx-auto text-primary" />
          <h2 className="mt-3 font-display text-xl font-semibold">No line yet</h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">Choose a plan to get your global number, SIP hardware credentials and in-app softphone — all provisioned automatically.</p>
          <Link to="/pricing" data-testid="device-choose-plan" className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground">
            View plans <Lightning size={16} weight="fill" />
          </Link>
        </div>

        {isOwner && (
          <div className="rounded-2xl border border-dashed border-primary/40 bg-primary/5 p-5" data-testid="device-owner-activate">
            <p className="text-sm font-semibold">Owner: activate a test line (no charge)</p>
            <p className="mt-1 text-xs text-muted-foreground">Provisions a real iYeneh calling credential + assigns an owned DID so you can test the softphone and desk phone.</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <select value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="device-plan-select" className="rounded-lg border border-input bg-background px-3 py-2 text-sm">
                <option value="residential">Residential</option>
                <option value="business">Business</option>
                <option value="enterprise">Enterprise</option>
              </select>
              <button onClick={activate} disabled={busy} data-testid="device-activate-btn" className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground disabled:opacity-60">
                {busy ? "Activating…" : "Activate my line"}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Provisioned
  const p = data.plan || {};
  return (
    <div className="mx-auto max-w-2xl space-y-6 p-1" data-testid="device-settings">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Device Settings</h1>
        <p className="mt-2 text-muted-foreground">Everything you need to connect your desk phone and softphone.</p>
      </div>

      {/* Plan + number */}
      <div className="rounded-2xl border border-primary/30 bg-gradient-to-b from-primary/10 to-transparent p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">{p.name} Plan</p>
            <p className="font-display text-lg font-semibold">{p.nickname}</p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-bold text-emerald-500" data-testid="device-status">
            <CheckCircle size={14} weight="fill" /> Active
          </span>
        </div>
        <div className="mt-4 flex items-center gap-3 rounded-xl bg-background/60 px-4 py-3">
          <Phone size={22} weight="fill" className="text-primary" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Your Global Number</p>
            <p className="font-mono-metric text-lg font-semibold" data-testid="device-did">{data.did}</p>
          </div>
          <button onClick={() => copy(data.did, "Number")} className="rounded-lg p-2 text-muted-foreground hover:text-primary" aria-label="Copy number"><Copy size={18} /></button>
        </div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <div className="rounded-lg bg-background/60 py-2"><p className="font-mono-metric text-lg font-semibold">{p.numbers}</p><p className="text-[11px] text-muted-foreground">Numbers</p></div>
          <div className="rounded-lg bg-background/60 py-2"><p className="font-mono-metric text-lg font-semibold">{p.extensions}</p><p className="text-[11px] text-muted-foreground">Extensions</p></div>
          <div className="rounded-lg bg-background/60 py-2"><p className="font-mono-metric text-lg font-semibold">{p.minutes?.toLocaleString?.()}</p><p className="text-[11px] text-muted-foreground">US/CA min</p></div>
        </div>
      </div>

      {/* Usage this cycle */}
      {usage?.provisioned && (
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="usage-dashboard">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold">This month's usage</h2>
            <span className="text-xs text-muted-foreground">{usage.cycle}</span>
          </div>
          <div className="space-y-3">
            <UsageBar label="US / Canada minutes" used={usage.minutes_used} total={usage.minutes_included} suffix="min" />
            <UsageBar label="World Calling Credit" used={usage.world_credit_used_cents} total={usage.world_credit_cents} money />
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground">Usage beyond your plan is billed automatically from your iYeneh Wallet.</p>
        </div>
      )}

      {/* Softphone dialer */}
      <Dialpad token={data.webrtc_token} did={data.did} sipUser={data.sip_username} sipPass={data.sip_password} onRefreshToken={refreshToken} />

      {/* SIP credentials for desk phone */}
      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="mb-3 flex items-center gap-2">
          <DeviceMobile size={20} weight="fill" className="text-primary" />
          <h2 className="font-display text-lg font-semibold">SIP Desk-Phone Credentials</h2>
        </div>
        <p className="mb-4 text-xs text-muted-foreground">Enter these into your iYeneh Hub or any SIP desk phone. Both your desk phone and softphone ring at the same time — answer on either.</p>
        <div className="space-y-3">
          <Row label="SIP Domain" value={data.sip_domain} onCopy={() => copy(data.sip_domain, "SIP Domain")} />
          <Row label="Username" value={data.sip_username} onCopy={() => copy(data.sip_username, "Username")} />
          <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card px-4 py-3">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Password</p>
              <p className="mt-0.5 truncate font-mono-metric text-sm text-foreground" data-testid="device-password">{showPw ? data.sip_password : "•".repeat(18)}</p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button onClick={() => setShowPw((s) => !s)} data-testid="toggle-password" className="rounded-lg p-2 text-muted-foreground hover:text-primary" aria-label="Show password">{showPw ? <EyeSlash size={16} /> : <Eye size={16} />}</button>
              <button onClick={() => copy(data.sip_password, "Password")} data-testid="copy-password" className="rounded-lg p-2 text-muted-foreground hover:text-primary" aria-label="Copy password"><Copy size={16} /></button>
            </div>
          </div>
        </div>
        <div className="mt-4 flex items-start gap-2 rounded-xl bg-secondary p-3 text-xs text-muted-foreground">
          <ShieldCheck size={16} className="mt-0.5 shrink-0 text-emerald-500" />
          Keep these private. Anyone with your SIP password can make calls billed to your account.
        </div>
      </div>

      <div className="flex items-center gap-2 rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
        <Headset size={18} className="text-primary" /> Need a desk phone? Grab a pre-configured iYeneh Hub in the <Link to="/app/shop" className="font-semibold text-primary hover:underline">Shop</Link>.
      </div>
    </div>
  );
}
